import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ConfigTypes, CTADataTypes, HashTypes } from '../types';
import { CA_MARKET, CAMPAIGN_ID, US_MARKET } from '../constants';
import getMarket from '../functions/getMarket';
import { setModalIsOpen } from '../redux/localConfigReducer';
import getAdobeCloudId from '../functions/getAdobeCloudId';
import useSourceOfLead from './useSourceOfLead';

// --------------------------------------------------------------------------------------------------- //
// Helper Functions
// --------------------------------------------------------------------------------------------------- //

const getQueryParam = (param: string): string | null => {
  return new URLSearchParams(window.location.search).get(param);
};

const parseMarket = (marketString: string | null | undefined): string | null => {
  if (!marketString) return null;
  return marketString.toLowerCase().includes('ca') ? CA_MARKET : US_MARKET;
};

const getDigitalDealerId = (): string | undefined => {
  return window?.digitalData?.dealer
    ? (window?.digitalData?.dealer as any)[0]?.dealerInfo?.dealerID
    : undefined;
};

const parseHashData = (hash: string): HashTypes => {
  if (hash.includes('_')) {
    return {
      presetForm: hash.split('_')[1],
      carline: '',
      audiCode: '',
      requestType: '',
    };
  }

  const params = new URLSearchParams(hash.substring(1));
  return {
    presetForm: params.get('form') ?? '',
    carline: params.get('carline') ?? '',
    audiCode: params.get('audiCode') ?? '',
    requestType: params.get('requestType') ?? '',
  };
};

const parsePresetFormFromId = (id: string): string | null => {
  return id.includes('_') ? id.split('_')[1] : null;
};

// --------------------------------------------------------------------------------------------------- //
// Hook
// --------------------------------------------------------------------------------------------------- //

const usePrepareConfig = (config: ConfigTypes) => {
  const [dataFromCta, setDataFromCta] = useState<CTADataTypes | null>(null);
  const [hashData, setHashdata] = useState<HashTypes | null>(null);

  const { generalSource } = useSourceOfLead();
  const dispatch = useDispatch();

  // --------------------------------------------------------------------------------------------------- //
  // CTA Click Handler
  // --------------------------------------------------------------------------------------------------- //

  const handleButtonClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const clickedButton = target.closest('[id^="digital-leads"]') as HTMLElement | null;

      if (!clickedButton) {
        const anchor = target as HTMLAnchorElement;
        if (anchor?.hash?.includes('#contact')) {
          setDataFromCta({ ctaInnerText: anchor.innerText });
        }
        return;
      }

      // Reset previous data
      setDataFromCta(null);
      setHashdata(null);

      const dataInfoAttr = clickedButton.getAttribute('data-info');
      const clickedCtaData = dataInfoAttr ? JSON.parse(dataInfoAttr) : null;
      const presetFormFromId = parsePresetFormFromId(clickedButton.id);

      const newCtaData: CTADataTypes = {
        ...clickedCtaData,
        ctaInnerText: clickedButton.innerText,
        ...(presetFormFromId && { presetForm: presetFormFromId }),
      };

      setDataFromCta(newCtaData);
      dispatch(setModalIsOpen());
    },
    [dispatch],
  );

  // --------------------------------------------------------------------------------------------------- //
  // Hash Change Handler
  // --------------------------------------------------------------------------------------------------- //

  const handleHashChange = useCallback(() => {
    const hashTrigger = window.location.hash;

    if (!hashTrigger.includes('#contact')) {
      return;
    }

    setDataFromCta(null);
    setHashdata(null);
    dispatch(setModalIsOpen());
    setHashdata(parseHashData(hashTrigger));
  }, [dispatch]);

  // --------------------------------------------------------------------------------------------------- //
  // Event Listeners
  // --------------------------------------------------------------------------------------------------- //

  useEffect(() => {
    document.addEventListener('click', handleButtonClick);
    return () => document.removeEventListener('click', handleButtonClick);
  }, [handleButtonClick]);

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [handleHashChange]);

  // --------------------------------------------------------------------------------------------------- //
  // Memoized Values
  // --------------------------------------------------------------------------------------------------- //

  // Market
  // ----------------------------- //
  const market = useMemo(() => {
    const ctaMarket = parseMarket(dataFromCta?.market);
    if (ctaMarket) return ctaMarket;

    const queryMarket = parseMarket(getQueryParam('market'));
    if (queryMarket) return queryMarket;

    if (config?.market) return config.market;

    return getMarket();
  }, [config?.market, dataFromCta?.market]);

  // Dealer Id
  // ----------------------------- //
  const dealerId = useMemo(() => {
    return (
      getDigitalDealerId() ||
      config?.dealerId ||
      dataFromCta?.dealerId ||
      getQueryParam('dealerId') ||
      null
    );
  }, [config?.dealerId, dataFromCta?.dealerId]);

  // Vehicle Id
  // ----------------------------- //
  const vehicleId = useMemo(() => {
    if (hashData?.presetForm) return '';
    return dataFromCta?.vehicleId || getQueryParam('vehicleId') || config?.vehicleId || '';
  }, [config?.vehicleId, dataFromCta?.vehicleId, hashData?.presetForm]);

  // Vehicle Type
  // ----------------------------- //
  const vehicleType = useMemo(() => {
    const queryMarket = getQueryParam('market');
    if (queryMarket) return queryMarket.includes('uc') ? 'used' : 'new';
    return dataFromCta?.vehicleType || 'new';
  }, [dataFromCta?.vehicleType]);

  // Preset form
  // ----------------------------- //
  const presetForm = useMemo(() => {
    if (hashData?.presetForm) return hashData.presetForm;
    if (dataFromCta?.presetForm) return dataFromCta.presetForm;

    const hasCustomForm = !!config?.customForm?.length;
    if (!hasCustomForm && config?.presetForm) return config.presetForm;

    return 'general';
  }, [
    config?.customForm?.length,
    config?.presetForm,
    dataFromCta?.presetForm,
    hashData?.presetForm,
  ]);

  // Custom Form
  // ----------------------------- //
  const customForm = useMemo(() => {
    const hasCustomForm = !!config?.customForm?.length;
    return !config?.presetForm && hasCustomForm ? config.customForm : [];
  }, [config?.customForm, config?.presetForm]);

  // --------------------------------------------------------------------------------------------------- //
  // Adobe Cloud and Campaign Ids
  // --------------------------------------------------------------------------------------------------- //

  const adobeCloudId = getAdobeCloudId();

  const campaignId = useMemo(() => {
    const queryParamCampaignId = getQueryParam('campaignId');
    if (queryParamCampaignId) return queryParamCampaignId;
    if (dataFromCta?.campaignId) return dataFromCta.campaignId;

    const isCanadianTier3 = market === CA_MARKET && generalSource === 'tier_3';
    if (isCanadianTier3) {
      return presetForm === 'general'
        ? CAMPAIGN_ID.vinSpecificContactDealer
        : CAMPAIGN_ID.genericContactDealer;
    }

    return '';
  }, [dataFromCta?.campaignId, generalSource, market, presetForm]);

  // --------------------------------------------------------------------------------------------------- //
  // Static Values
  // --------------------------------------------------------------------------------------------------- //

  const successModalConfig = {
    successCustomHeading: config?.headingCustomText,
    successCustomBody: config?.bodyCustomText,
    successCustomCtaVerbiage: config?.ctaVerbiageCustom,
    successCustomCtaUrl: config?.ctaUrlCustom,
  };

  // Request Type
  // ----------------------------- //
  const requestType = hashData?.requestType;

  // Audi Code
  // ----------------------------- //
  const audiCode = hashData?.audiCode ?? null;

  // Carline
  // ----------------------------- //
  const carline = hashData?.carline ?? null;

  // Cta Inner text
  // ----------------------------- //
  const ctaInnerText = dataFromCta?.ctaInnerText ?? null;

  // Offers
  // ----------------------------- //
  const offers = dataFromCta?.offers ?? null;

  // Other config values
  // ----------------------------- //
  const tcpaConsent = config?.tcpaConsent;
  const dealerConsent = config?.dealerConsent;
  const dealerConsentFr = config?.dealerConsentFr;
  const ccpaDisclaimer = config?.ccpaDisclaimer;
  const ccpaConsent = config?.ccpaConsent;
  const customDepartmentName = config?.departmentName ?? null;
  const customPhoneNumber = config?.phoneNumberCMS?.toString() ?? null;
  const topicOfInterests = config?.topicOfInterestList;

  return {
    market,
    dealerId,
    vehicleId,
    vehicleType,
    modelSalesCode: config?.modelSalesCode,
    presetForm,
    customForm,
    adobeCloudId,
    requestType,
    audiCode,
    carline,
    ctaInnerText,
    campaignId,
    isOfferPage: !!dataFromCta?.offers,
    offers,
    tcpaConsent,
    dealerConsent,
    dealerConsentFr,
    ccpaDisclaimer,
    ccpaConsent,
    customDepartmentName,
    customPhoneNumber,
    successModalConfig,
    topicOfInterests,
  };
};

export default usePrepareConfig;
